import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Library, Trash2, Eye } from "lucide-react";
import PackageView from "@/components/agency/PackageView";

export default function LibraryView({ brand }) {
  const [pkgs, setPkgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    if (!brand) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/brands/${brand.brand_id}/packages`);
      setPkgs(res.data);
    } catch (e) { console.error("Erro ao carregar pacotes:", e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brand?.brand_id]);

  const remove = async (id, ev) => {
    ev.stopPropagation();
    await api.delete(`/packages/${id}`);
    setPkgs((p) => p.filter((x) => x.package_id !== id));
    toast.success("Pacote removido.");
  };

  return (
    <div>
      <div className="mb-6">
        <p className="overline text-emerald-400 mb-1">Biblioteca de conteúdos</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">Pacotes gerados{brand ? ` — ${brand.nome}` : ""}</h2>
        <p className="text-sm text-slate-400 mt-1">Todos os pacotes produzidos pela agência para esta marca.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
      ) : pkgs.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-400">
          <Library className="w-10 h-10 mx-auto mb-3 text-slate-600" />
          Nenhum pacote salvo ainda. Use "Criar conteúdo para minha marca".
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pkgs.map((p) => (
            <Dialog key={p.package_id}>
              <DialogTrigger asChild>
                <div data-testid={`library-card-${p.package_id}`}
                  className="glass rounded-2xl overflow-hidden cursor-pointer hover:brightness-110 hover:border-emerald-500/40 transition-all group">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="w-full h-36 object-cover" />
                  ) : (
                    <div className="w-full h-36 bg-slate-900 flex items-center justify-center">
                      <Library className="w-8 h-8 text-slate-700" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="border-emerald-700/50 text-emerald-300 text-[10px]">
                        {p.hook_tipo || "conteúdo"}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <Eye className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400 transition-colors" />
                        <button onClick={(ev) => remove(p.package_id, ev)} data-testid={`del-pkg-${p.package_id}`}
                          className="text-slate-500 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <p className="text-sm font-medium line-clamp-2 leading-snug">{p.ideia}</p>
                    <p className="text-xs text-slate-500 mt-2 font-mono">
                      {new Date(p.created_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                </div>
              </DialogTrigger>
              <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-display text-xl">{p.brand_nome} — Pacote de conteúdo</DialogTitle>
                </DialogHeader>
                <PackageView pkg={p} brand={brand} onScheduled={() => {}} />
              </DialogContent>
            </Dialog>
          ))}
        </div>
      )}
    </div>
  );
}
